import ArrowDown from "@uui/ArrowDown";
import ArrowUp from "@uui/ArrowUp";
import { shallow } from "zustand/shallow";
import { createWithEqualityFn } from "zustand/traditional";

import type {
  MeetingReportDialog,
  MeetingReportNudge,
} from "@app/analytics/MeetingReport/types";

interface MeetingReportUiStateValues {
  dialog: MeetingReportDialog | null;
  main: {
    nudge: MeetingReportNudge | null;
  };
  sidebar: {
    nudge: MeetingReportNudge | null;
    open: boolean;
  };
}

interface MeetingReportUiState extends MeetingReportUiStateValues {
  /* Opens the sidebar if it's closed. Noop if the sidebar is already open */
  openSidebar: () => void;
  resetState: () => void;

  setDialog: (dialog: MeetingReportDialog | null) => void;
  setNudge: (
    type: "main" | "sidebar",
    nudge: MeetingReportNudge | null
  ) => void;
  setSidebarOpen: (open: boolean) => void;
}

const DEFAULT_STATE: MeetingReportUiStateValues = {
  dialog: null,
  main: {
    nudge: null,
  },
  sidebar: {
    nudge: null,
    open: true,
  },
};

const NUDGE_ICON_BY_PLACEMENT = {
  top: <ArrowUp />,
  bottom: <ArrowDown />,
};

const useMeetingReportUiStore = createWithEqualityFn<MeetingReportUiState>(
  (set) => ({
    ...DEFAULT_STATE,
    openSidebar: () =>
      set((state) =>
        state.sidebar.open
          ? state
          : { sidebar: { ...state.sidebar, open: true } }
      ),
    resetState: () => set(DEFAULT_STATE),

    setDialog: (dialog) => set({ dialog }),
    setNudge: (type, nudge) =>
      set((state) => ({
        [type]: {
          ...state[type],
          nudge: nudge?.placement
            ? { ...nudge, startIcon: NUDGE_ICON_BY_PLACEMENT[nudge.placement] }
            : nudge,
        },
      })),
    setSidebarOpen: (open) =>
      set((state) => ({
        sidebar: { ...state.sidebar, open },
      })),
  }),
  shallow
);

export default useMeetingReportUiStore;
